/**
 * Deterministic responsive layout engine for MyVault canvases.
 *
 * This is a pure implementation of the mapping algorithm frozen in the
 * MyVault Canvas product/UX specification, section 7.2 ("Desired -- a
 * deterministic responsive model"). It maps a scope's elements from their
 * authored desktop coordinates `(c, r, w, h, o)` to a target breakpoint, so the
 * builder preview, runtime, and public viewer all render identically.
 *
 * The engine is intentionally free of React and DOM concerns: it consumes plain
 * coordinate items and returns plain coordinate items. Callers adapt canvas
 * elements to `ResponsiveItem` (see `fromLayout`) and map the DOM afterwards.
 *
 * Rules (spec 7.2), verbatim in behaviour:
 *  - desktop: coordinates are used as authored.
 *  - phone (C = 1): order by (r, c, o) ascending; stack single full-width column
 *    with cumulative row positions -- rowCursor starts at 1, each element takes
 *    grid_column 1, column_span 1, grid_row = rowCursor, keeps row_span = h, then
 *    rowCursor += h.
 *  - tablet (or any C): scale each span w' = clamp(round(w * C / G), 1, C), then
 *    shelf-pack in (r, c, o) order tracking shelf height -- colCursor/shelfRow/
 *    shelfMaxH; wrap advances shelfRow by shelfMaxH (not by 1) so a tall element
 *    on a shelf is never overlapped by the next shelf.
 *  - nested containers: recurse inside each container's scope with effective
 *    child columns Cc = max(1, round(W * C / G)) and the container's authored
 *    child-column count as the child scope's G.
 */

import { BreakpointKind } from "./breakpoints";

/**
 * One element in a layout scope, in the authored (desktop) coordinate system.
 * Indices are 1-based, matching the spec's worked examples. Spans are in grid
 * cells.
 */
export interface ResponsiveItem {
  id: string;
  /** grid_column `c` (1-based). */
  col: number;
  /** grid_row `r` (1-based). */
  row: number;
  /** column_span `w`. */
  w: number;
  /** row_span `h`. */
  h: number;
  /** display_order `o`; ties in (r, c) are broken by this. */
  order: number;
  /**
   * Nested container children, authored on the container's own child grid.
   * Present only for container-like elements.
   */
  children?: ResponsiveItem[];
  /**
   * The container's authored child-column count (its nested scope's `G`).
   * Defaults to `DEFAULT_CHILD_GRID_COLUMNS` when absent. Ignored for elements
   * with no children. This is a coordinate-model field, deliberately named
   * independently of any product property; the product adapter
   * (`authoredChildColumnsOf` in CanvasRenderer, reading `internalGrid.columns`)
   * is what maps a product container onto this value.
   */
  authoredChildColumns?: number;
}

/** One element after mapping to a target breakpoint. */
export interface MappedItem {
  id: string;
  col: number;
  row: number;
  w: number;
  h: number;
  children?: MappedItem[];
  /**
   * Effective column count of this item's nested child scope at the target
   * breakpoint (the `C` its children were shelf-packed against): the container's
   * authored child columns on desktop, 1 on phone, and `nestedChildColumns` on
   * tablet. Present only when the item has children. The renderer uses this to
   * size the nested CSS grid (`repeat(childColumns, 1fr)`) so nested elements are
   * placed on the same effective grid the engine mapped them onto.
   */
  childColumns?: number;
}

/**
 * Default authored child-column count for a nested container scope. Matches the
 * canvas grid default of 12 columns (spec 7.1 / DEFAULT_GRID_CONFIG); the
 * desired model derives the nested grid from the same responsive model rather
 * than a separate hardcoded container grid (spec 7.2 item 3).
 */
export const DEFAULT_CHILD_GRID_COLUMNS = 12;

/** Clamp `v` into the inclusive range [lo, hi]. */
function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/**
 * Scale one span against the authored scope grid `G` (not a constant 12):
 * `w' = clamp(round(w * C / G), 1, C)` (spec 7.2, tablet rule step 1).
 * `Math.round` is round-half-up; the spec's worked examples contain no exact
 * half cases.
 */
export function rescaleSpan(w: number, C: number, G: number): number {
  return clamp(Math.round((w * C) / G), 1, C);
}

/**
 * Effective child columns for a nested container spanning authored `w` columns
 * of a scope with effective columns `C` and authored columns `G`:
 * `Cc = max(1, round(w * C / G))` (spec 7.2, nested-containers rule). For valid
 * (in-bounds) input `w <= G`, so `Cc <= C`.
 */
export function nestedChildColumns(w: number, C: number, G: number): number {
  return Math.max(1, Math.round((w * C) / G));
}

/**
 * Deterministic ordering key for a scope: `(r, c, o)` ascending (spec 7.2).
 * Returns a new sorted array; the input is not mutated.
 */
function orderedByRowColOrder(items: ResponsiveItem[]): ResponsiveItem[] {
  return [...items].sort((a, b) => {
    if (a.row !== b.row) {
      return a.row - b.row;
    }
    if (a.col !== b.col) {
      return a.col - b.col;
    }
    return a.order - b.order;
  });
}

function childCols(item: ResponsiveItem): number {
  return item.authoredChildColumns ?? DEFAULT_CHILD_GRID_COLUMNS;
}

/** desktop: coordinates as authored; children recurse as identity. */
function mapDesktop(items: ResponsiveItem[]): MappedItem[] {
  return items.map((item) => {
    const mapped: MappedItem = {
      id: item.id,
      col: item.col,
      row: item.row,
      w: item.w,
      h: item.h,
    };
    if (item.children && item.children.length > 0) {
      mapped.children = mapDesktop(item.children);
      // Desktop keeps the authored child grid, so the child scope's effective
      // column count is the container's authored child-column count.
      mapped.childColumns = childCols(item);
    }
    return mapped;
  });
}

/** phone (C = 1): single full-width column, cumulative rows by row_span. */
function mapPhone(items: ResponsiveItem[]): MappedItem[] {
  const ordered = orderedByRowColOrder(items);
  const out: MappedItem[] = [];
  let rowCursor = 1;
  for (const item of ordered) {
    const mapped: MappedItem = {
      id: item.id,
      col: 1,
      row: rowCursor,
      w: 1,
      h: item.h,
    };
    if (item.children && item.children.length > 0) {
      // A nested container on phone stacks its children the same way (C = 1).
      mapped.children = mapPhone(item.children);
      mapped.childColumns = 1;
    }
    out.push(mapped);
    rowCursor += item.h;
  }
  return out;
}

/** tablet / any C >= 1: shelf-pack tracking shelf height (spec 7.2). */
function mapShelfPack(
  items: ResponsiveItem[],
  C: number,
  G: number,
): MappedItem[] {
  const ordered = orderedByRowColOrder(items);
  const out: MappedItem[] = [];
  let colCursor = 1;
  let shelfRow = 1;
  let shelfMaxH = 0;
  for (const item of ordered) {
    const w = rescaleSpan(item.w, C, G);
    // Wrap when the element does not fit the columns remaining on this shelf.
    if (w > C - colCursor + 1) {
      shelfRow += shelfMaxH;
      colCursor = 1;
      shelfMaxH = 0;
    }
    const mapped: MappedItem = {
      id: item.id,
      col: colCursor,
      row: shelfRow,
      w,
      h: item.h,
    };
    if (item.children && item.children.length > 0) {
      const Cc = nestedChildColumns(item.w, C, G);
      mapped.children = mapShelfPack(item.children, Cc, childCols(item));
      mapped.childColumns = Cc;
    }
    out.push(mapped);
    colCursor += w;
    shelfMaxH = Math.max(shelfMaxH, item.h);
  }
  return out;
}

/**
 * Map one scope's elements to a target breakpoint against an explicit effective
 * column count `C` and authored column count `G`.
 *
 * This is the general per-scope primitive. `mapScope` calls it for the root
 * scope with the breakpoint's standard `C`; the renderer calls it directly for
 * NESTED scopes, whose effective columns are the spec-7.2 nested value
 * `Cc = nestedChildColumns(...)` rather than the standard breakpoint `C` (8 at
 * tablet). `C` is consulted only for the tablet shelf-pack; desktop is identity
 * and phone is always a single column, so both ignore `C`.
 *
 * @param items authored (desktop) elements of the scope
 * @param kind  target breakpoint (desktop | tablet | phone)
 * @param C     effective column count for this scope at this breakpoint
 * @param G     authored column count of this scope (its own grid)
 */
export function mapScopeWithColumns(
  items: ResponsiveItem[],
  kind: BreakpointKind,
  C: number,
  G: number,
  compactDesktop = false,
): MappedItem[] {
  switch (kind) {
    case "desktop":
      return compactDesktop ? mapShelfPack(items, C, G) : mapDesktop(items);
    case "phone":
      return mapPhone(items);
    case "tablet":
      return mapShelfPack(items, C, G);
    default: {
      const never: never = kind;
      return never;
    }
  }
}

/**
 * Map one root scope's elements to a target breakpoint.
 *
 * @param items    authored (desktop) elements of the root scope
 * @param kind     target breakpoint (desktop | tablet | phone)
 * @param gridColumns the canvas's authored `grid_columns` (the root scope `G`,
 *                 and the desktop effective column count `C`)
 */
export function mapScope(
  items: ResponsiveItem[],
  kind: BreakpointKind,
  gridColumns: number,
): MappedItem[] {
  // Root scope: tablet effective columns are the fixed token 8; desktop/phone
  // ignore C. G is the canvas's authored grid_columns.
  return mapScopeWithColumns(
    items,
    kind,
    kind === "tablet" ? 8 : gridColumns,
    gridColumns,
  );
}
